import { useState, useRef } from 'react';
import { Heart, CircleNotch } from '@phosphor-icons/react';
import { useFavorites } from '../context/FavoritesContext';
import { useAuth } from '../context/AuthContext';
import telegramService from '../services/telegram';

const FavoriteButton = ({ placeId, variant = 'text', size = 'medium', className = '' }) => {
  const { isFavorite, toggleFavorite } = useFavorites();
  const { isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [optimisticState, setOptimisticState] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const clickTimeoutRef = useRef(null);

  // If not authenticated, don't show button
  if (!isAuthenticated) {
    return null;
  }

  // Determine current state (optimistic or actual)
  const currentIsFavorite = optimisticState !== null ? optimisticState : isFavorite(placeId);

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent multiple rapid clicks (debounce)
    if (isLoading) return;

    // Clear any pending timeout
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }

    // Haptic feedback
    telegramService.hapticFeedback('light');

    // Trigger animation
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 400);

    // Optimistic update
    const newState = !currentIsFavorite;
    setOptimisticState(newState);
    setIsLoading(true);

    // Debounce: wait 300ms before making API call
    clickTimeoutRef.current = setTimeout(async () => {
      try {
        await toggleFavorite(placeId);

        // Success - clear optimistic state
        setOptimisticState(null);

        // Additional haptic feedback on success
        telegramService.hapticFeedback('success');
      } catch (error) {
        // Rollback optimistic update
        setOptimisticState(null);

        // Error haptic feedback
        telegramService.hapticFeedback('error');
      } finally {
        setIsLoading(false);
      }
    }, 300);
  };

  // Text variant (for PlacePage) — ключевое действие страницы: всегда
  // с подписью. Режим compact удалён в Этапе 5: вид кнопки не должен
  // зависеть от того, сколько контактов заполнено в Strapi
  if (variant === 'text') {
    return (
      <button
        onClick={handleClick}
        disabled={isLoading}
        className={`
          h-11 px-3.5 flex-shrink-0
          rounded-md
          flex items-center justify-center gap-1.5
          text-label
          transition-all duration-300
          active:scale-95
          disabled:opacity-50
          disabled:cursor-not-allowed
          ${currentIsFavorite
            ? 'bg-theme-favorite-bg text-theme-favorite-text'
            : 'bg-theme-bg-secondary text-theme-text-primary'
          }
          ${className}
        `}
        aria-label={currentIsFavorite ? 'Удалить из избранного' : 'Добавить в избранное'}
      >
        <Heart
          size={24}
          weight={currentIsFavorite ? 'fill' : 'regular'}
          className={`
            transition-transform duration-300
            ${isAnimating ? 'scale-[1.2]' : 'scale-100'}
          `}
        />
        <span className={`whitespace-nowrap transition-opacity duration-300 ${isAnimating ? 'opacity-80' : 'opacity-100'}`}>
          {currentIsFavorite ? 'В избранном' : 'Сохранить'}
        </span>
      </button>
    );
  }

  // Card variant (for PlaceCard) - keep old behavior
  const sizeClasses = {
    small: 'w-8 h-8',
    medium: 'w-10 h-10',
    large: 'w-12 h-12',
    card: 'w-[40px] h-[40px]',
  };

  // Ряд 16/20/24 — брендбук 9.3
  const iconSizes = {
    small: 16,
    medium: 20,
    large: 24,
    card: 20,
  };

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      style={{
        // Без backdrop-filter: размытие роняет плавность слайдеров
        // на Android — брендбук 5.4. Фон вместо этого плотнее:
        // .28 давала белому глифу лишь 1.98 на светлом снимке
        background: 'rgba(0, 0, 0, 0.6)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
      }}
      className={`
        ${sizeClasses[size]}
        rounded-full
        flex
        items-center
        justify-center
        transition-all
        duration-200
        active:scale-95
        disabled:opacity-50
        disabled:cursor-not-allowed
        ${className}
      `}
      aria-label={currentIsFavorite ? 'Удалить из избранного' : 'Добавить в избранное'}
    >
      {isLoading ? (
        <CircleNotch
          size={iconSizes[size]}
          weight="bold"
          className="animate-spin text-white"
        />
      ) : (
        // Добавленное — залитое и красное: цветом состояние читается
        // мгновенно, одним весом разницу на 20px не заметить
        <Heart
          size={iconSizes[size]}
          weight={currentIsFavorite ? 'fill' : 'regular'}
          className={currentIsFavorite ? 'text-theme-favorite-on-photo' : 'text-white'}
        />
      )}
    </button>
  );
};

export default FavoriteButton;
